import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Building2, Receipt, Percent, LogOut, ArrowLeft, BookOpen, Hash,
} from "lucide-react";
import { useModuleAuth } from "@/hooks/useModuleAuth";
import { AuthCheckSkeleton } from "@/components/AuthCheckSkeleton";

const AccountsLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Pro-only per-module auth wall (no-op on Core).
  const checking = useModuleAuth({ role: "accounts", loginPath: "/accounts-login" });

  const navItems = [
    { path: "/accounts/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/accounts/customers", label: "Customers", icon: Users },
    { path: "/accounts/vendors", label: "Vendors", icon: Building2 },
    { path: "/accounts/tax-rates", label: "Tax Rates", icon: Percent },
    { path: "/accounts/voucher-types", label: "Voucher Types", icon: Receipt },
    { path: "/accounts/invoice-settings", label: "Invoice #", icon: Hash },
    { path: "/accounts/vouchers", label: "Vouchers", icon: BookOpen },
  ];

  // Auth removed: Accounts module is openly accessible.

  const handleLogout = async () => {
    try { const { logout } = await import("@/utils/auth"); await logout(); } catch { /* */ }
    navigate("/");
  };

  if (checking) return <AuthCheckSkeleton />;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "radial-gradient(ellipse at center, #0f1729 0%, #000000 70%)" }}>
      <nav className="bg-black/90 shadow-2xl border-b-2 border-blue-500/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14">
            <div className="flex items-center space-x-6">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/accounts/dashboard")}>
                <BookOpen className="h-5 w-5 text-blue-400" />
                <span className="text-blue-400 font-bold text-sm hidden sm:block">ACCOUNTS</span>
              </div>
              {navItems.map(item => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <button key={item.path} onClick={() => navigate(item.path)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-all ${active ? "text-cyan-300 bg-cyan-500/15 ring-1 ring-cyan-400/50" : "text-gray-400 hover:text-white hover:bg-white/5"}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <Icon className="h-3.5 w-3.5" />
                    {item.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-red-400 text-xs" data-testid="accounts-logout-btn">
                <LogOut className="h-3.5 w-3.5 mr-1" /> Logout
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-gray-400 hover:text-white text-xs">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Home
              </Button>
            </div>
          </div>
        </div>
      </nav>
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>
    </div>
  );
};

export default AccountsLayout;
